export const INSIGHTS_PAGE_SIZE = 5;
const FRESH_MS = 60_000;
const RETAIN_MS = 5 * 60_000;
const TIMEOUT_MS = 15_000;
const RETRY_DELAY_MS = 400;
const MAX_PAGES = 20;

function canRetry(error) {
  return ['TypeError', 'SyntaxError', 'AbortError', 'UnexpectedResponseError'].includes(error?.name)
    || [408, 500, 502, 503, 504].includes(error?.status);
}

// Only public article summaries are cached, in memory for this tab. Prefetch
// and navigation share one request; leaving a page does not cancel that work.
export function createInsightsCache(fetchPage) {
  const entries = new Map();
  const keyFor = (lang, page) => `${lang}:${page}:${INSIGHTS_PAGE_SIZE}`;

  function trim() {
    for (const [key, entry] of entries) {
      if (entries.size <= MAX_PAGES) break;
      if (!entry.task && !entry.foreground) entries.delete(key);
    }
  }

  function peek(lang, page = 1) {
    const entry = entries.get(keyFor(lang, page));
    if (!entry?.data) return null;
    const age = Date.now() - entry.updatedAt;
    if (age >= RETAIN_MS) return null;
    // An expired empty result must be confirmed again before showing empty UI.
    if (age >= FRESH_MS && entry.data.data.length === 0) return null;
    return entry.data;
  }

  function getEntry(lang, page) {
    const key = keyFor(lang, page);
    let entry = entries.get(key);
    if (!entry) {
      entry = { data: null, updatedAt: 0, task: null, foreground: null };
      entries.set(key, entry);
    }
    return entry;
  }

  function isFresh(entry) {
    return entry.data && Date.now() - entry.updatedAt < FRESH_MS;
  }

  function start(entry, lang, page, timeoutMs = TIMEOUT_MS) {
    const key = keyFor(lang, page);
    const controller = new AbortController();
    const task = { controller, timer: setTimeout(() => controller.abort(), timeoutMs), promise: null };
    entry.task = task;
    task.promise = Promise.resolve()
      .then(() => fetchPage({ lang, page, limit: INSIGHTS_PAGE_SIZE, status: 'published', signal: controller.signal }))
      .then(result => {
        if (controller.signal.aborted) throw new DOMException('Request aborted', 'AbortError');
        if (!Array.isArray(result.data)) throw new TypeError('Invalid article list');
        const data = { ...result, page };
        // An invalidated request must never repopulate the cache after an edit.
        if (entries.get(key) === entry && entry.task === task) {
          entry.data = data;
          entry.updatedAt = Date.now();
        }
        return data;
      })
      .finally(() => {
        clearTimeout(task.timer);
        if (entry.task === task) entry.task = null;
        trim();
      });
    trim();
    return task;
  }

  function load(lang, page = 1) {
    const key = keyFor(lang, page);
    const entry = getEntry(lang, page);
    if (entry.foreground) return entry.foreground;
    if (isFresh(entry)) return Promise.resolve(entry.data);

    const deadline = Date.now() + TIMEOUT_MS;
    let task = entry.task;
    if (!task || task.controller.signal.aborted) task = start(entry, lang, page);
    // Adopting speculative work starts a full foreground budget. Repeated
    // consumers join entry.foreground and cannot keep extending this deadline.
    clearTimeout(task.timer);
    task.timer = setTimeout(() => task.controller.abort(), TIMEOUT_MS);
    const foreground = task.promise
      .catch(async error => {
        const delay = Math.max(RETRY_DELAY_MS, error?.retryAfterMs || 0);
        if (!canRetry(error) || entries.get(key) !== entry || deadline - Date.now() <= delay) throw error;
        await new Promise(resolve => setTimeout(resolve, delay));
        if (entries.get(key) !== entry) throw new DOMException('Cache invalidated', 'AbortError');
        const remaining = deadline - Date.now();
        if (remaining <= 0) throw error;
        // At most one retry, sharing the original foreground deadline. Passive
        // prefetches never retry on their own or turn a failure into empty data.
        return start(entry, lang, page, remaining).promise;
      })
      .finally(() => {
        if (entry.foreground === foreground) entry.foreground = null;
        trim();
      });
    entry.foreground = foreground;
    return foreground;
  }

  function prefetch(lang, page = 1) {
    // Speculative failures stay silent and are never cached. A visible page
    // uses load() directly so its own failure/retry UI remains available.
    const entry = getEntry(lang, page);
    if (isFresh(entry)) return Promise.resolve(entry.data);
    if (entry.foreground) return entry.foreground.catch(() => {});
    const task = entry.task && !entry.task.controller.signal.aborted
      ? entry.task : start(entry, lang, page);
    return task.promise.catch(() => {});
  }

  function invalidate() {
    for (const entry of entries.values()) entry.task?.controller.abort();
    entries.clear();
  }

  return { peek, load, prefetch, invalidate };
}
